import { ReactElement, SyntheticEvent, useCallback } from 'react';
import { Stack } from '@mui/material';
import { TabContext } from '@mui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserTie as DriverIcon,
  faVectorSquare as LogisticZoneIcon,
  faStoreAlt as CompanyIcon,
} from '@fortawesome/free-solid-svg-icons';

import { TabData, TabPanelData } from '~globals/types';
import LoadingFallback from '~components/LoadingFallback';

import { DashboardVariantV2CarrierContentInTransitTrackingPanelId } from '../../types';
import { useDashboardVariantV2CarrierContentInTransitTrackingContext } from '../../Context';

import DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabDrivers from './Drivers';
import DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabZones from './Zones';
import DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabCompanies from './Companies';

import {
  DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabList,
  DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTab,
  DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabsPanelContainer,
  DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabPanel,
} from './styles';

const TABS_LIST: TabData<DashboardVariantV2CarrierContentInTransitTrackingPanelId>[] =
  [
    {
      id: 'drivers',
      label: 'Por choferes',
      icon: <FontAwesomeIcon icon={DriverIcon} />,
    },
    {
      id: 'zones',
      label: 'Por zonas',
      icon: <FontAwesomeIcon icon={LogisticZoneIcon} />,
    },
    {
      id: 'companies',
      label: 'Por empresa',
      icon: <FontAwesomeIcon icon={CompanyIcon} />,
    },
  ];

// eslint-disable-next-line max-len
const TABS_LIST_PANELS: TabPanelData<DashboardVariantV2CarrierContentInTransitTrackingPanelId>[] =
  [
    {
      id: 'drivers',
      panel:
        DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabDrivers,
    },
    {
      id: 'zones',
      panel:
        DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabZones,
    },
    {
      id: 'companies',
      panel:
        DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabCompanies,
    },
  ];

const DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabs =
  (): ReactElement => {
    const {
      activeTab: { tabId: activeTab, setTabId: setActiveTab },
      tabsData: { loading: loadingTabData },
    } = useDashboardVariantV2CarrierContentInTransitTrackingContext();

    const handleChangeActiveTab = useCallback(
      (
        _e: SyntheticEvent,
        newValue: DashboardVariantV2CarrierContentInTransitTrackingPanelId,
      ) => {
        setActiveTab(newValue);
      },
      [setActiveTab],
    );

    return (
      <Stack spacing={3} flex={1} sx={{ overflowY: 'hidden' }}>
        <TabContext value={activeTab}>
          <DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabList
            onChange={handleChangeActiveTab}
          >
            {TABS_LIST.map(({ label, id, icon }) => (
              <DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTab
                key={`tracking-tab-${id}`}
                label={label}
                value={id}
                icon={icon}
                iconPosition="start"
              />
            ))}
          </DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabList>

          <DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabsPanelContainer>
            {TABS_LIST_PANELS.map(({ id, panel: Panel }) => (
              <DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabPanel
                key={`tracking-tab-panel-${id}`}
                value={id}
              >
                {loadingTabData && <LoadingFallback />}

                {!loadingTabData && <Panel />}
              </DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabPanel>
            ))}
          </DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabsPanelContainer>
        </TabContext>
      </Stack>
    );
  };

export default DashboardVariantV2CarrierContentInTransitTabTrackingSelectionTabs;
